import { useState } from "react";
import { FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { postRequest } from "../../../helpers/api.helper";

type SuspendModalProps = {
  closeSuspendModal: () => void;
  profile: any;
  setIsSuspendModalOpen: (open: boolean) => void;
};

const SuspendModal = ({
  closeSuspendModal,
  profile,
  setIsSuspendModalOpen,
}: SuspendModalProps) => {
  const [suspendDescription, setSuspendDescription] = useState("");
  const [suspendLoading, setSuspendLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSuspendSubmit = async () => {
    if (!suspendDescription.trim()) {
      toast.error("⚠️ Please enter a suspension reason");
      return;
    }

    if (!profile?._id) {
      toast.error("⚠️ Invalid user profile");
      return;
    }

    setSuspendLoading(true);
    try {
      const payload = {
        userId: profile?._id, // 🧩 backend expects userId
        suspend: true, // 🧩 mark as suspended
        reason: suspendDescription, // 🧩 suspension reason
      };
      const response = await postRequest(
        `/bureau/profiles/suspend`, // 👈 endpoint should point to /profiles/suspend
        payload,
      );
      if (response.success) {
        toast.success("✅ Profile suspended successfully!");
        setIsSuspendModalOpen(false); // Close modal after success
      } else {
        toast.error(
          response.data?.message ||
            "Failed to suspend profile. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error suspending profile:", error);
      toast.error("❌ Error suspending profile. Please try again later.");
    } finally {
      setSuspendLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-red-600">Suspend Profile</h2>
          <button
            onClick={closeSuspendModal}
            className="text-gray-500 hover:text-gray-700"
          >
            <FaTimes size={20} />
          </button>
        </div>

        <div className="mb-4 p-3 bg-gray-50 rounded">
          <p className="text-sm text-gray-600">Suspending Profile:</p>
          <p className="font-semibold">{profile.fullName}</p>
          <p className="text-sm text-gray-600">
            Marital ID: {profile.martialId}
          </p>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason for Suspending *
          </label>
          <textarea
            value={suspendDescription}
            onChange={(e) => setSuspendDescription(e.target.value)}
            placeholder="Please provide more details about your Account Suspension..."
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
            rows={4}
          />
        </div>

        <div className="flex space-x-4">
          <button
            onClick={closeSuspendModal}
            className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSuspendSubmit}
            disabled={loading || !suspendDescription.trim()}
            className="flex-1 py-2 px-4 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {suspendLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Suspending...
              </>
            ) : (
              "Suspend"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuspendModal;
